// =============================================================================
// io/parsers/jigsaw.ts — Jigsaw Trading "MyTrades" report (XLSX).
// -----------------------------------------------------------------------------
// PORTED FROM `tradecoach-saas/app/index.html` — semantics, not code (D3):
// `parseJigsawXlsxRows` and PATH 4 of the workbook dispatcher.
//
// The last format the shipping client reads and this one did not.
//
// ── ONE ROW PER CLOSED ROUND TRIP, AND ONLY ONE PRICE ──
//
// From Josh's real report: `Account, AccTyp, MktTyp, Symbol, Open Date,
// Open Time, Closed Date, Closed Time, TimeIn, TradeType, Pos, MaxQty, Qty,
// AvgPrice, Curr, OpenPnL, ClosedPnL, NetPnL, MAE, MFE, Commision, Comments,
// HashTag`.
//
// ⚠ THERE IS NO EXIT PRICE. `AvgPrice` is the average ENTRY and that is the only
// price in the file, so the exit is DERIVED from the reported net:
//
//     points = (NetPnL + Commission) / ($/point × qty)
//     exit   = entry ± points
//
// That keeps the round trip self-consistent — the stored points, gross and net
// all reconcile with Jigsaw's own number instead of contradicting it.
//
// ⚠ AND IT IS DELIBERATELY **NOT** ROUNDED TO THE TICK. `AvgPrice` is an
// average, so the implied average exit legitimately sits between ticks. Snapping
// it would drift the reconstructed P&L away from Jigsaw's reported net on any
// scaled position — which is most of them.
//
// ⚠ The sign repair in `buildImportedTrade` is a NO-OP here by construction, not
// by luck: the exit is derived from the net, so the computed gross is exactly
// `net + commission` and the expected net is exactly the supplied one. Left
// running anyway, matching the old client, because it costs nothing and an
// unknown contract (no $/point) skips it regardless.
//
// ── ⚠ MAE / MFE ARRIVE IN TICKS, NOT POINTS ──
//
// Every other source in this client reports excursion in POINTS. Jigsaw reports
// ticks, so the values are multiplied by the contract's tick size. Getting this
// wrong is silent and four-fold on an ES-family contract: a 44-tick adverse
// excursion stored as 44 POINTS is $2,200 of heat on a trade that risked $550.
//
// ⚠ It also means a contract this client cannot resolve gets NO excursion at
// all rather than a wrong one — no tick size, no conversion.
//
// ⚠ OPEN QUESTION, FLAGGED RATHER THAN GUESSED: whether Jigsaw's tick counts are
// per-contract or aggregated across the position. On Josh's file one row agrees
// exactly with per-contract (5 lots, $100 net on MES = 4 points = 16 ticks, and
// MFE reads 16) and the other does not obviously fit either reading. The old
// client treats them as per-contract; this port does the same, so the two agree.
// Resolving it needs a Jigsaw report next to a chart, not more arithmetic.
// =============================================================================

import { getCol, numCol, numColSigned, type Row } from './columns';
import { buildImportedTrade, fullContractSym, type ImportedTrade } from './sierra';
import { findContract, type ContractSpec } from '../../domain/trade';
import { parseDateOnly, parseTimeOnly } from './delimited';

/** Whether a sheet's headers are a Jigsaw "MyTrades" report. */
export function looksLikeJigsaw(headers: readonly string[]): boolean {
  const h = new Set(headers.map((x) => x.trim().toLowerCase()));
  return h.has('avgprice') && h.has('netpnl') && h.has('symbol');
}

export function parseJigsawRows(
  rows: readonly Row[],
  catalogue: readonly ContractSpec[],
  today: string,
): ImportedTrade[] {
  const trades: ImportedTrade[] = [];

  rows.forEach((r, idx) => {
    const symRaw = String(getCol(r, 'Symbol', 'Instrument') || '').trim();
    if (!symRaw) return;

    const c = findContract(symRaw, catalogue);
    const dpp = c?.dollarsPerPoint ?? 0;
    const tick = c?.tickSize ?? 0;

    // ⚠ `Pos` carries "Buy"/"Sell"; `TradeType` is blank on the real report and
    // is kept as a fallback because the old client lists it.
    const side: 'long' | 'short' =
      /sell|short/i.test(String(getCol(r, 'Pos', 'TradeType', 'Side', 'Direction') || ''))
        ? 'short' : 'long';

    // ⚠ `MaxQty` FIRST. `Qty` is the position size NOW — "0.00" on every closed
    // row — so reading it first would make every trade a 1-lot.
    const qty = Math.abs(parseFloat(
      String(getCol(r, 'MaxQty', 'Qty', 'Quantity', 'Volume') || '0').replace(/,/g, ''),
    )) || 1;

    const avgPrice = numCol(r, 'AvgPrice', 'Avg Price', 'Price');
    if (!avgPrice) return;

    // ⚠ "Commision" — Jigsaw's own spelling, and it has to come first.
    const commission = Math.abs(numCol(r, 'Commision', 'Commission', 'Fees'));
    const net = numColSigned(r, 'NetPnL', 'Net PnL', 'ClosedPnL', 'Net P/L', 'PnL');

    let exit = avgPrice;
    if (dpp > 0 && qty > 0) {
      const pts = (net + commission) / (dpp * qty);
      exit = side === 'long' ? avgPrice + pts : avgPrice - pts;
      // Full precision, NOT tick-rounded — see the header.
      exit = Math.round(exit * 1e6) / 1e6;
    }

    const maeT = numCol(r, 'MAE');
    const mfeT = numCol(r, 'MFE');
    const maePoints = (tick > 0 && isFinite(maeT) && maeT !== 0) ? Math.abs(maeT * tick) : null;
    const mfePoints = (tick > 0 && isFinite(mfeT) && mfeT !== 0) ? Math.abs(mfeT * tick) : null;

    const date = parseDateOnly(getCol(r, 'Open Date', 'OpenDate', 'Date'), today);
    const openTimeRaw = String(getCol(r, 'Open Time', 'OpenTime') || '');

    trades.push(buildImportedTrade({
      date,
      timeIn: parseTimeOnly(openTimeRaw),
      timeOut: parseTimeOnly(getCol(r, 'Closed Time', 'ClosedTime', 'Close Time')),
      contract: c?.symbol || symRaw.toUpperCase(),
      contractFull: fullContractSym(symRaw),
      side, qty,
      entry: avgPrice, exit,
      fees: commission,
      netPnl: net,
      // ⚠ 🚨 `dpp: 0` IS DELIBERATE, AND IT IS NOT AN OVERSIGHT.
      //
      // The old client omits the field entirely (`d.dpp || 0` then reads 0);
      // this client's `BuildArgs` requires it, so the same value is stated
      // explicitly — which is better, because the intent is now readable
      // instead of being an absence. The zero is load-bearing. With no
      // $/point, `buildImportedTrade` cannot compute a gross from the prices, so
      // it falls back to `net + fees` — which is EXACTLY Jigsaw's own number.
      // Pass `dpp` and the gross is instead rebuilt from the DERIVED exit, and
      // the derived exit was rounded to six places, so a 15-lot MES trade whose
      // reported net is $81.25 stores a gross of $81.249975.
      //
      // Nothing would ever surface that as an error. It is a $0.000025
      // disagreement between two fields that are supposed to reconcile, on
      // every scaled Jigsaw trade, and it took the differential comparison
      // against the shipping client to see it at all. My first version passed
      // `dpp`; the unit tests did not care because the 5-lot fixture divides
      // exactly.
      //
      // It also skips the sign repair, which is correct here for the same reason
      // it is a no-op elsewhere in this parser: the exit is derived FROM the net,
      // so the net cannot disagree with it.
      dpp: 0,
      sourceAccount: getCol(r, 'Account', 'AccountID', 'AccountId') || null,
      openFillId: null,
      // ⚠ Carries the ROW INDEX, like every other paired source: two identical
      // scalps are two trades, and a content-only fingerprint collapses them.
      closeFillId: `jigsaw-rt:${idx}:${date}:${openTimeRaw}:${avgPrice}:${qty}`,
      maePoints, mfePoints,
      // Jigsaw reports no time-to-extreme.
      maeSecs: null, mfeSecs: null,
    }));
  });

  return trades.filter((t) => t.contract);
}
